/**
 * Plant-selection context and its hook.
 *
 * Separate from the provider for the same reason `site-context-value.ts` is: each module
 * exports one kind of thing, which is what React Fast Refresh needs to hot-reload either.
 *
 * A PLANT is a part of a site — the block of equipment that combines on its own bus. It sits
 * one level BELOW the site selector and above everything else, so the header reads
 * "Jamshoro ▸ Plant 3" and every site-scoped screen narrows to that plant's devices.
 */
import { createContext, useContext } from 'react';
import type { GroupingSpec, PlantGroup, PlantGrouping } from '@/lib/plant-groups';

export interface PlantContextValue {
  /**
   * Every plant RECORDED for the selected site, plus the equipment filed into none of them.
   *
   * Read from the `plants` table since 2026-09-18 — see `app/plant-context.tsx` for what this
   * used to be and why the change was not cosmetic. `PlantGroup.id` is a `plants.id` uuid, so
   * it is the same identifier a site grant names; it used to be a derived topology node id,
   * which named nothing outside this file.
   */
  grouping: PlantGrouping;
  /** What this plant type calls its parts — "Plant" on a generation site, "Train" on ICT. */
  spec: GroupingSpec;
  /**
   * Whether the console should offer the control at all.
   *
   * DERIVED from the fleet's own state, never configured: a site with fewer than two recorded
   * plants has nothing to narrow, and a control that cannot narrow anything is hidden rather
   * than disabled — the same rule the industry switcher follows (`Docs/08-views/SITES.md` §3).
   *
   * Jamshoro has five plants and so shows the control; JAZZMSC5 has none and so does not. That
   * used to depend on whether anybody had drawn a busbar; it now depends on whether anybody has
   * recorded a plant, which is a question an operator can answer on the site's Devices tab.
   */
  visible: boolean;
  /** `null` means ALL — the whole site, exactly as every screen behaved before this existed. */
  selectedId: string | null;
  selected: PlantGroup | null;
  select: (id: string | null) => void;
  /**
   * The devices in scope, or `null` for the whole site.
   *
   * NULL IS NOT AN EMPTY LIST, and consumers must not treat it as one. `null` means "do not
   * narrow"; an empty array would mean "narrow to nothing" and would blank every screen. The
   * distinction is the whole contract of this field.
   */
  deviceIds: readonly number[] | null;
  /** True while the plant records or the device inventory are still loading. */
  loading: boolean;
  /**
   * The device inventory stopped short of the end of its cursor.
   *
   * `useAllDevices` follows the keyset to the end but stops at a page cap, and a short
   * inventory means a plant's `deviceIds` is short. By `plantAdmits`' site-wide clause a device
   * in no plant is admitted by every plant, so the missing devices appear under ALL of them
   * rather than vanishing — wrong in the widening direction, which is the safe one for an alarm
   * board and still a wrong answer worth surfacing.
   *
   * Exists because the opposite was shipped: `SiteAccessSection` asked `/devices` for
   * `limit: 500` against a cap of 200, got a validation failure, and drew every plant with no
   * devices in it, with nothing on screen to say why. GUIDELINES §11: never read a capped list
   * without reading its cap.
   */
  inventoryTruncated: boolean;
}

const EMPTY_GROUPING: PlantGrouping = {
  groups: [],
  ungroupedNodeIds: [],
  ungroupedDeviceIds: [],
};

export const PLANT_CONTEXT_FALLBACK: PlantContextValue = {
  grouping: EMPTY_GROUPING,
  spec: { noun: 'Group', plural: 'Groups', why: 'No site selected.' },
  visible: false,
  selectedId: null,
  selected: null,
  select: () => {},
  deviceIds: null,
  loading: false,
  inventoryTruncated: false,
};

export const PlantContext = createContext<PlantContextValue>(PLANT_CONTEXT_FALLBACK);

/**
 * The selected plant.
 *
 * Safe to call outside the provider: it returns "no plants, nothing narrowed", so a screen
 * rendered in a test or a story behaves exactly as it did before plants existed instead of
 * throwing. That is deliberate — this is a narrowing convenience, not a precondition for
 * any screen to work.
 */
export function usePlantContext(): PlantContextValue {
  return useContext(PlantContext);
}
